import React, { useState, useEffect, useMemo } from 'react';
import { getProviders, createProvider, updateProvider, deleteProvider } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { PageTransition, StaggerContainer, StaggerItem } from '../components/motion/Animations';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import PageHelpButton from '../components/help/PageHelpButton';
import {
  Package, Plus, Search, Mail, Phone, MapPin, Edit2, Trash2, Building2,
  CreditCard, User, Globe, FileText, Upload, ArrowUpDown,
} from 'lucide-react';

const CATEGORIES = [
  { value: 'materiales', label: 'Materiales' },
  { value: 'servicios', label: 'Servicios' },
  { value: 'tecnologia', label: 'Tecnología' },
  { value: 'logistica', label: 'Logística' },
  { value: 'suministros', label: 'Suministros' },
  { value: 'profesional', label: 'Servicios Profesionales' },
  { value: 'otro', label: 'Otro' },
];

const ProvidersPage = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState(() => {
    try { return localStorage.getItem('providers:sortBy') || 'name_asc'; } catch { return 'name_asc'; }
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: '', tax_id: '', email: '', phone: '', address: '', city: '',
    postal_code: '', country: 'España', contact_person: '', bank_name: '',
    bank_account: '', category: '', payment_terms: '', notes: '', website: ''
  });

  useEffect(() => { loadProviders(); }, []);

  const loadProviders = async () => {
    try {
      const { data } = await getProviders();
      setProviders(data);
    } catch { toast.error('Error al cargar proveedores'); }
    finally { setLoading(false); }
  };

  const openModal = (provider = null) => {
    if (provider) {
      setEditing(provider);
      setFormData({
        name: provider.name || '', tax_id: provider.tax_id || '', email: provider.email || '',
        phone: provider.phone || '', address: provider.address || '', city: provider.city || '',
        postal_code: provider.postal_code || '', country: provider.country || 'España',
        contact_person: provider.contact_person || '', bank_name: provider.bank_name || '',
        bank_account: provider.bank_account || '', category: provider.category || '',
        payment_terms: provider.payment_terms || '', notes: provider.notes || '',
        website: provider.website || ''
      });
    } else {
      setEditing(null);
      setFormData({ name: '', tax_id: '', email: '', phone: '', address: '', city: '',
        postal_code: '', country: 'España', contact_person: '', bank_name: '',
        bank_account: '', category: '', payment_terms: '', notes: '', website: '' });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await updateProvider(editing.id, formData);
        toast.success('Proveedor actualizado');
      } else {
        await createProvider(formData);
        toast.success('Proveedor creado');
      }
      loadProviders();
      setIsModalOpen(false);
    } catch { toast.error('Error al guardar proveedor'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este proveedor?')) return;
    try {
      await deleteProvider(id);
      toast.success('Proveedor eliminado');
      loadProviders();
    } catch { toast.error('Error al eliminar'); }
  };

  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    const out = providers.filter((p) => {
      if (!q) return true;
      const hay = `${p.name || ''} ${p.email || ''} ${p.tax_id || ''} ${p.city || ''} ${p.contact_person || ''} ${p.category || ''} ${p.phone || ''} ${p.notes || ''}`.toLowerCase();
      return hay.includes(q);
    });
    const cmp = (a, b, key, dir) => {
      const va = a?.[key] ?? '';
      const vb = b?.[key] ?? '';
      if (typeof va === 'number' && typeof vb === 'number') return dir * (va - vb);
      return dir * String(va).localeCompare(String(vb), 'es', { numeric: true });
    };
    const [field, direction] = sortBy.endsWith('_asc') ? [sortBy.replace('_asc', ''), 1] : [sortBy.replace('_desc', ''), -1];
    return [...out].sort((a, b) => cmp(a, b, field, direction));
  }, [providers, searchTerm, sortBy]);

  if (loading) {
    return <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B4A]"></div>
    </div>;
  }

  return (
    <PageTransition>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight text-stone-950" data-testid="providers-title">Proveedores</h1>
              <p className="text-stone-400 text-sm mt-1">Gestiona tu red de proveedores</p>
            </div>
            <PageHelpButton moduleKey="providers" variant="icon" />
          </div>
          <div className="flex gap-2">
            <Link to="/import?entity=providers">
              <Button variant="outline" className="rounded-xl border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="import-providers-button">
                <Upload className="w-4 h-4 mr-2" /> Importar Excel
              </Button>
            </Link>
            <Button onClick={() => openModal()} className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300" data-testid="add-provider-button">
              <Plus className="w-4 h-4 mr-2" /> Nuevo Proveedor
            </Button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input placeholder="Buscar por nombre, NIF, email, ciudad, categoría…" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-11 rounded-xl bg-stone-50 border-transparent" data-testid="search-providers-input" />
          </div>
          <div className="relative sm:w-56">
            <ArrowUpDown className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none z-10" />
            <Select
              value={sortBy}
              onValueChange={(v) => {
                setSortBy(v);
                try { localStorage.setItem('providers:sortBy', v); } catch {}
              }}
            >
              <SelectTrigger className="pl-10 rounded-xl bg-stone-50 border-transparent" data-testid="providers-sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name_asc">Nombre (A→Z)</SelectItem>
                <SelectItem value="name_desc">Nombre (Z→A)</SelectItem>
                <SelectItem value="total_purchases_desc">Compras (mayor→menor)</SelectItem>
                <SelectItem value="expenses_count_desc">Nº de gastos</SelectItem>
                <SelectItem value="created_at_desc">Más recientes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {filtered.length > 0 ? (
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((provider) => (
              <StaggerItem key={provider.id}>
                <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-5 card-hover" data-testid={`provider-card-${provider.id}`}>
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-50 rounded-2xl flex items-center justify-center">
                        <Package className="w-5 h-5 text-[#FF6B4A]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-stone-900">{provider.name}</h3>
                        {provider.tax_id && <p className="text-xs text-stone-400">{provider.tax_id}</p>}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => openModal(provider)} className="p-1.5 hover:bg-orange-50 rounded-xl transition-colors" data-testid={`edit-provider-${provider.id}`}>
                        <Edit2 className="w-4 h-4 text-stone-400" />
                      </button>
                      <button onClick={() => handleDelete(provider.id)} className="p-1.5 hover:bg-red-50 rounded-xl transition-colors" data-testid={`delete-provider-${provider.id}`}>
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </div>
                  {provider.category && (
                    <span className="inline-block text-[10px] px-2.5 py-0.5 rounded-full bg-sky-50 text-sky-600 font-medium mb-3">
                      {CATEGORIES.find(c => c.value === provider.category)?.label || provider.category}
                    </span>
                  )}
                  <div className="space-y-1.5 text-sm">
                    {provider.contact_person && <div className="flex items-center gap-2 text-stone-500"><User className="w-3.5 h-3.5 text-stone-300" />{provider.contact_person}</div>}
                    {provider.email && <div className="flex items-center gap-2 text-stone-500"><Mail className="w-3.5 h-3.5 text-stone-300" />{provider.email}</div>}
                    {provider.phone && <div className="flex items-center gap-2 text-stone-500"><Phone className="w-3.5 h-3.5 text-stone-300" />{provider.phone}</div>}
                    {(provider.city || provider.address) && <div className="flex items-center gap-2 text-stone-500"><MapPin className="w-3.5 h-3.5 text-stone-300" />{[provider.address, provider.city].filter(Boolean).join(', ')}</div>}
                    {provider.bank_account && (
                      <div className="flex items-center gap-2 text-stone-500" title={provider.bank_account}>
                        <CreditCard className="w-3.5 h-3.5 text-stone-300" />
                        <span className="font-mono text-[11px] truncate">{provider.bank_account}</span>
                      </div>
                    )}
                    {provider.website && (
                      <div className="flex items-center gap-2 text-stone-500">
                        <Globe className="w-3.5 h-3.5 text-stone-300" />
                        <span className="truncate">{provider.website}</span>
                      </div>
                    )}
                  </div>
                  {(provider.expenses_count > 0 || provider.total_purchases > 0) && (
                    <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-between text-xs text-stone-400">
                      <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> {provider.expenses_count || 0} gastos</span>
                      {provider.total_purchases > 0 && <span className="font-semibold text-stone-600">{new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: 'EUR' }).format(provider.total_purchases)}</span>}
                    </div>
                  )}
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        ) : (
          <div className="text-center py-16 bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100">
            <Package className="w-14 h-14 mx-auto mb-4 text-stone-200" />
            <h3 className="text-lg font-semibold text-stone-800 mb-1">Sin proveedores</h3>
            <p className="text-stone-400 mb-6">Añade tu primer proveedor</p>
            <Button onClick={() => openModal()} className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full" data-testid="add-first-provider-button">
              <Plus className="w-4 h-4 mr-2" /> Añadir Proveedor
            </Button>
          </div>
        )}

        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading font-extrabold">{editing ? 'Editar Proveedor' : 'Nuevo Proveedor'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label>Nombre / Razón Social *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required
                    className="rounded-xl bg-stone-50 border-transparent" data-testid="provider-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>NIF/CIF</Label>
                  <Input value={formData.tax_id} onChange={(e) => setFormData({...formData, tax_id: e.target.value})}
                    className="rounded-xl bg-stone-50 border-transparent" placeholder="B12345678" data-testid="provider-taxid-input" />
                </div>
                <div className="space-y-2">
                  <Label>Categoría</Label>
                  <Select value={formData.category || undefined} onValueChange={(v) => setFormData({...formData, category: v})}>
                    <SelectTrigger className="rounded-xl bg-stone-50 border-transparent" data-testid="provider-category-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Persona de Contacto</Label>
                  <Input value={formData.contact_person} onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                    className="rounded-xl bg-stone-50 border-transparent" data-testid="provider-contact-input" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="rounded-xl bg-stone-50 border-transparent" data-testid="provider-email-input" />
                </div>
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="rounded-xl bg-stone-50 border-transparent" data-testid="provider-phone-input" />
                </div>
                <div className="space-y-2">
                  <Label>Web</Label>
                  <Input value={formData.website} onChange={(e) => setFormData({...formData, website: e.target.value})}
                    className="rounded-xl bg-stone-50 border-transparent" data-testid="provider-website-input" />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Dirección</Label>
                  <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})}
                    className="rounded-xl bg-stone-50 border-transparent" data-testid="provider-address-input" />
                </div>
                <div className="space-y-2">
                  <Label>Ciudad</Label>
                  <Input value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})}
                    className="rounded-xl bg-stone-50 border-transparent" data-testid="provider-city-input" />
                </div>
                <div className="space-y-2">
                  <Label>Código Postal</Label>
                  <Input value={formData.postal_code} onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                    className="rounded-xl bg-stone-50 border-transparent" data-testid="provider-postal-input" />
                </div>
                <div className="space-y-2">
                  <Label>Banco</Label>
                  <Input value={formData.bank_name} onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                    className="rounded-xl bg-stone-50 border-transparent" placeholder="CaixaBank, BBVA..." data-testid="provider-bank-input" />
                </div>
                <div className="space-y-2">
                  <Label>IBAN / Cuenta</Label>
                  <Input value={formData.bank_account} onChange={(e) => setFormData({...formData, bank_account: e.target.value})}
                    className="rounded-xl bg-stone-50 border-transparent" placeholder="ES12 3456..." data-testid="provider-iban-input" />
                </div>
                <div className="space-y-2">
                  <Label>Condiciones de Pago</Label>
                  <Input value={formData.payment_terms} onChange={(e) => setFormData({...formData, payment_terms: e.target.value})}
                    className="rounded-xl bg-stone-50 border-transparent" placeholder="30 días, al contado..." data-testid="provider-terms-input" />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Notas</Label>
                  <Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    className="rounded-xl bg-stone-50 border-transparent" rows={2} data-testid="provider-notes-input" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="flex-1 rounded-full">Cancelar</Button>
                <Button type="submit" className="flex-1 bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full" data-testid="save-provider-button">
                  {editing ? 'Guardar' : 'Crear'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </PageTransition>
  );
};

export default ProvidersPage;
